<template>

  <div>

  <!-- XOL-606: at xs / sm / md (vue-mq keys are upper bounds: a 375px phone is `sm`) each Rotate
       button sat in a 12.5% column, about 33px wide there, while a nowrap text button needs about
       60px: the left one spilled into the toolbar and the right one ran past the cell edge. The
       controls stack instead: the page toolbar on one line, the Rotate pair on its own line (left
       at the left edge, right at the right edge, as on desktop), both above the preview. At lg /
       hg the row of columns is exactly as before. -->
  <div v-if="isMediumOrLess" class="pdf-controls">

    <pdf-toolbar v-if="pageCount" v-model="currentPage" :total-pages="pageCount" />

    <div v-if="showRotate" class="pdf-rotate">
      <el-button type="text" icon="fa fa-undo" aria-label="Rotate left" @click="rotate -=90">&nbsp;Rotate</el-button>
      <el-button type="text" aria-label="Rotate right" @click="rotate +=90">Rotate&nbsp;<i class="fa fa-undo fa-flip-horizontal" aria-hidden="true"></i></el-button>
    </div>

  </div>

  <el-row v-else>

    <el-col v-if="showRotate" :span="3" align="left">
      <el-button type="text" icon="fa fa-undo" aria-label="Rotate left" @click="rotate -=90">&nbsp;Rotate</el-button>
    </el-col>

    <el-col :span="showRotate ? 18 : 24">

  <pdf-toolbar v-if="pageCount" v-model="currentPage" :total-pages="pageCount" />

  </el-col>

    <el-col v-if="showRotate" :span="3" align="right">
      <el-button type="text" aria-label="Rotate right" @click="rotate +=90">Rotate&nbsp;<i class="fa fa-undo fa-flip-horizontal" aria-hidden="true"></i></el-button>
    </el-col>
  </el-row>


  <!-- PDF goes here -->
  <el-row type="flex" justfy="center">
      <el-col :span="24" class="pdf-stage">

      <!-- XOL-382: :key tears the vue-pdf instance down on document switch,
           cancelling its in-flight pdf.js render task so nothing from the
           old document outlives the switch.
           page-loaded is deliberately NOT bound: echoing render completions
           back into currentPage (the vue-pdf README pattern) races user
           navigation — a slow page render finishing after the user has
           already paged on (or switched documents) would drag currentPage
           back to the stale page. currentPage drives the viewer one-way;
           internal document links come back via link-clicked only. -->
      <!-- XOL-393: a PDF that failed or is still downloading used to render as blank
           space with no feedback ("PDFs don't render at times; large files don't
           respond well"). vue-pdf emits `error` on a failed getDocument and
           `loaded` when the document resolves, so surface both.

           The placeholder OVERLAYS the viewer rather than hiding it. vue-pdf sizes its
           canvas from `canvasElt.offsetWidth` when it renders a page, so a
           `display:none` viewer (v-show / v-if) measures 0 and renders at scale 0 —
           it only recovers because vue-resize-sensor happens to fire when the element
           becomes visible. Keeping the viewer in flow means the very first render is
           correctly sized, which matters most for exactly the large/slow documents
           this change is meant to help. -->
      <div v-if="loadError" class="pdf-state pdf-state--error">
        <i class="fa fa-triangle-exclamation"></i>&nbsp;Couldn't display this PDF.
        <el-button type="text" @click="retry">Try again</el-button>
      </div>

      <div v-else-if="!loaded" v-loading="true" element-loading-text="Loading PDF…" class="pdf-state pdf-state--overlay" />

      <pdf
        v-show="!loadError"
        :key="reloadKey"
        :src="pdfSrc"
        :rotate="rotate"
        :page="currentPage"
        @num-pages="pageCount = $event"
        @loaded="loaded = true"
        @error="onError"
        @link-clicked="currentPage = $event"
        style="width: 100%; height: auto;" />
    </el-col>

  </el-row>

  <pdf-toolbar v-if="showPageNumbersBottom && pageCount" v-model="currentPage" :total-pages="pageCount" />

</div>
</template>

<style scoped>
  /* XOL-606: the Rotate pair on its own line at xs / sm / md. */
  .pdf-rotate {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0 0 8px;
  }
  .pdf-rotate .el-button + .el-button {
    margin-left: 0;
  }
  /* Anchor for the loading overlay — the viewer itself must stay in normal flow so
     vue-pdf can measure a real offsetWidth on its first render. */
  .pdf-stage {
    position: relative;
  }
  .pdf-state {
    min-height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    font-size: 13px;
  }
  /* Covers the viewer while the document downloads, without removing it from flow. */
  .pdf-state--overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    min-height: 180px;
    background: #fff;
    z-index: 1;
  }
  .pdf-state--error {
    color: #f56c6c;
  }
</style>

<script>
import pdf from 'vue-pdf'
import PdfToolbar from '@/components/atoms/PdfToolbar'

export default {
  name: 'pdf-viewer',

  components: {
    pdf,
    PdfToolbar
  },

  props: {
    url: {
      type: String,
      required: true
    },

    showRotate: {
      type: Boolean,
      default: true
    },

    showPageNumbersBottom: {
      type: Boolean,
      default: false
    }

  },

  computed: {
    // XOL-144: PDF.js (via vue-pdf) uses CORS-mode XHR internally. Unlike
    // `<img src>` (no-CORS, browser attaches cookies automatically when
    // SameSite=None;Secure), CORS-mode requests default to credentials='omit'
    // — so the xolas_auth cookie is NOT sent and the request 401s. Passing
    // an object src lets us forward `withCredentials: true` to PDF.js's
    // getDocument(), which propagates to the underlying XHR.
    //
    // XOL-382: only do this for our own API. Help manuals live on an external
    // CDN (ImageKit) which answers with `Access-Control-Allow-Origin: *` —
    // browsers reject wildcard ACAO on credentialed requests, so sending
    // withCredentials:true on every URL broke Help doc loading entirely.
    pdfSrc () {
      const endpoint = this.getStore().endpoint
      return {
        url: this.url,
        withCredentials: Boolean(endpoint && this.url && this.url.startsWith(endpoint))
      }
    }
  },

  watch: {
    pageCount: function (val) {
      // XOL-382: guard against val === 0 — that's the transient "no document
      // loaded yet" state set by the url watcher below on document switch,
      // not a real page count to clamp against. Without this guard, resetting
      // pageCount to 0 forces currentPage down to 0 too (undoing the url
      // watcher's reset to page 1) whenever the two watchers flush together.
      if (val && this.currentPage > val) {
        this.currentPage = val
      }
    },

    // XOL-382: Help.vue reuses one un-keyed <pdf-viewer> instance across
    // documents. Without this, currentPage/rotate carry over from the
    // previously viewed document. Resetting pageCount to 0 also unmounts
    // the v-if="pageCount" PdfToolbar instances so their internal state
    // remounts fresh once the new document emits num-pages.
    url: function () {
      this.currentPage = 1
      this.pageCount = 0
      this.rotate = 0
      this.loaded = false
      this.loadError = false
      this.reloadKey = this.url
    }
  },

  methods: {
    onError (err) {
      // eslint-disable-next-line no-console
      console.error('PdfViewer: failed to load', this.url, err)
      this.loadError = true
      this.loaded = false
    },

    // Remount the vue-pdf instance so pdf.js re-runs getDocument from scratch.
    retry () {
      this.loadError = false
      this.loaded = false
      this.currentPage = 1
      this.pageCount = 0
      this.reloadKey = this.url + '#retry-' + Date.now()
    }
  },

  data () {
    return {
      rotate: 0,
      currentPage: 1,
      pageCount: 0,
      loaded: false,
      loadError: false,
      reloadKey: this.url
    }
  }
}

</script>
